import React from 'react';
import { Play, Pause, Square, Volume2, ChevronDown } from 'lucide-react';
import { useSpeech } from '@shared/hooks/useSpeech';
import { Button } from '@shared/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@shared/components/ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@shared/components/ui/select';
import { cn } from '@shared/lib/utils';
import { toast } from 'sonner';

interface SpeechControlsProps {
  getText: () => string;
}

export default function SpeechControls({ getText }: SpeechControlsProps) {
  const { speak, pause, resume, stop, status, isSupported, voices, selectedVoice, setVoice, rate, setRate } =
    useSpeech();

  if (!isSupported) return null;

  const handlePlay = () => {
    if (status === 'paused') {
      resume();
      return;
    }
    const text = getText();
    if (!text.trim()) {
      toast.info('Nothing to read');
      return;
    }
    speak(text);
  };

  const isSpeaking = status === 'speaking';
  const isPaused = status === 'paused';
  const isActive = isSpeaking || isPaused;

  return (
    <div className="flex items-center gap-1">
      {/* Play / Pause button */}
      <Button
        variant="ghost"
        size="sm"
        className={cn(
          'gap-1.5 h-8 text-xs',
          isActive ? 'text-primary' : 'text-muted-foreground'
        )}
        onClick={isSpeaking ? pause : handlePlay}
        title={isSpeaking ? 'Pause' : isPaused ? 'Resume' : 'Read aloud'}
      >
        {isSpeaking ? (
          <Pause className="h-4 w-4" />
        ) : (
          <Volume2 className="h-4 w-4" />
        )}
        <span>{isSpeaking ? 'Pause' : isPaused ? 'Resume' : 'Listen'}</span>
      </Button>

      {/* Stop button — only when active */}
      {isActive && (
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8 text-muted-foreground"
          onClick={stop}
          title="Stop"
        >
          <Square className="h-3.5 w-3.5" />
        </Button>
      )}

      {/* Settings popover */}
      <Popover>
        <PopoverTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 text-muted-foreground"
            title="Speech settings"
          >
            <ChevronDown className="h-3.5 w-3.5" />
          </Button>
        </PopoverTrigger>
        <PopoverContent align="end" className="w-64 space-y-3 p-3">
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Speech Settings</p>

          {/* Voice picker */}
          {voices.length > 0 && (
            <div className="space-y-1">
              <label className="text-xs text-muted-foreground">Voice</label>
              <Select
                value={selectedVoice?.name ?? ''}
                onValueChange={name => {
                  const v = voices.find(v => v.name === name);
                  if (v) setVoice(v);
                }}
              >
                <SelectTrigger className="h-8 text-xs">
                  <SelectValue placeholder="Select voice" />
                </SelectTrigger>
                <SelectContent className="max-h-48">
                  {voices.map(v => (
                    <SelectItem key={v.name} value={v.name} className="text-xs">
                      {v.name} ({v.lang})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Speed slider */}
          <div className="space-y-1">
            <div className="flex items-center justify-between">
              <label className="text-xs text-muted-foreground">Speed</label>
              <span className="text-xs font-medium">{rate.toFixed(1)}×</span>
            </div>
            <input
              type="range"
              min={0.5}
              max={2}
              step={0.1}
              value={rate}
              onChange={e => setRate(Number(e.target.value))}
              className="w-full accent-hintword-orange"
            />
            <div className="flex justify-between text-xs text-muted-foreground/60">
              <span>0.5×</span>
              <span>2×</span>
            </div>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}
